/**
 * Greybox Three.js renderer.
 * Accepts sim snapshots only — no tick / gate / combat logic.
 * Transform juice: pedestal hologram, absorb beams, FOV punch, mecha spawn.
 */
import * as THREE from "three";
import { createPortraitCamera, snapCentroidFollow, updateCentroidFollow, setPortraitAspect, portraitFovForAspect, } from "./camera.js";
import { createBarrelSystem, defaultCanisterPlacementsForWaves, } from "./barrels.js";
import { createCombatFx } from "./combatFx.js";
import { createCountPopupFx, } from "./countPopup.js";
import { createGateBreakFx } from "./gateBreak.js";
import { createMechaMesh } from "./mecha.js";
import { createLevelMarkers, } from "./levelMarkers.js";
import { createPedestalHologram, HOLOGRAM_SOLIDIFY_SECONDS, } from "./pedestalHologram.js";
import { rebuildPathBridge, } from "./pathBridge.js";
import { advanceShake, createShakeState, resetShake, sampleShakeOffset, triggerShake, } from "./shake.js";
import { centroidFromSnapshot, DEFAULT_PATH_HALF_WIDTH, } from "./snapshot.js";
import { createUnitInstances } from "./units.js";
import { createHordeInstances, } from "./zombies.js";
import { GREYBOX } from "./materials.js";
import { applyCameraFov, createAbsorbVfx, createTransformJuiceState, currentFov, triggerTransformJuice, ABSORB_DURATION_SECONDS, } from "./transformFx.js";
export class GreyboxRenderer {
    scene;
    camera;
    renderer;
    root;
    units;
    horde;
    mecha;
    combat;
    gateBreak;
    /** Pooled floating count deltas (losses / absorb / extra gains). */
    countPopups;
    absorb;
    /** Explosive canisters (barrels) — red/purple + lightning, chainable. */
    barrels;
    /** Desire object: silver pedestal + rotating holographic mecha. */
    pedestalHolo;
    juice;
    /** Screen shake trauma (explosions / horde / transform). Identifier: shake. */
    shake;
    follow = { x: 0, y: 0, z: 0 };
    cameraOpts;
    pathHalfWidth;
    /** Aspect-adapted base FOV (portrait modest; landscape wider). */
    baseFov;
    path = null;
    markers = null;
    lastPathLength = -1;
    lastSnapshot = null;
    lastDrawn = 0;
    lastHordeDrawn = 0;
    disposed = false;
    displayTime = 0;
    wasInMecha = false;
    pedestalZ = null;
    pedestalX = 0;
    rocketAcc = 0;
    /** 1 = pure hologram, 0 = solidified / hidden (real mecha owns silhouette). */
    hologramSolidifyT = 1;
    /** Wave defs for horde density (from setLevelMarkers). */
    hordeWaves = [];
    /** Gate ids that already played gateBreak this run (edge-detect). */
    gateBreakPlayed = new Set();
    /** Detonations from last applySnapshot (shell applies SIM aggregate damage). */
    lastDetonations = [];
    /** Squad hit-flash envelope 1 → 0 (horde contact). */
    hitFlashT = 0;
    /** Last sim N for edge-detect count popups when shell omits notify. */
    lastCountN = null;
    /** Reused absorb beam origins — avoids slice/map alloc during transform juice. */
    beamOriginScratch = Array.from({ length: 16 }, () => ({ x: 0, y: 0.85, z: 0 }));
    /** Draw calls reported by WebGLRenderer.info after last render(). */
    lastDrawCalls = 0;
    lastTriangles = 0;
    constructor(canvas, options = {}) {
        this.pathHalfWidth = options.pathHalfWidth ?? DEFAULT_PATH_HALF_WIDTH;
        this.cameraOpts = options.camera ?? {};
        const w = Math.max(1, canvas.clientWidth || canvas.width || 390);
        const h = Math.max(1, canvas.clientHeight || canvas.height || 844);
        // Portrait-first base FOV; landscape widens via portraitFovForAspect
        this.baseFov = portraitFovForAspect(w / h, this.cameraOpts);
        this.juice = createTransformJuiceState(this.baseFov);
        this.scene = new THREE.Scene();
        this.scene.name = "greyboxScene";
        const fogColor = options.clearColor ?? GREYBOX.fog;
        this.scene.background = new THREE.Color(fogColor);
        // Atmospheric distance fog — bridge vanishes at horizon (ad frame)
        this.scene.fog = new THREE.Fog(fogColor, 22, 110);
        this.camera = createPortraitCamera(w / h, this.cameraOpts);
        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: options.antialias ?? true,
            alpha: false,
            powerPreference: "high-performance",
        });
        const dpr = typeof globalThis !== "undefined" &&
            "devicePixelRatio" in globalThis &&
            typeof globalThis.devicePixelRatio ===
                "number"
            ? globalThis.devicePixelRatio
            : 1;
        this.renderer.setPixelRatio(Math.min(dpr, 2));
        this.renderer.setSize(w, h, false);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.root = new THREE.Group();
        this.root.name = "greyboxRoot";
        this.scene.add(this.root);
        const hemi = new THREE.HemisphereLight(0xb0c4de, 0x1f2937, 0.85);
        this.scene.add(hemi);
        const key = new THREE.DirectionalLight(0xffffff, 1.05);
        key.position.set(6, 18, 4);
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        key.shadow.camera.near = 1;
        key.shadow.camera.far = 80;
        key.shadow.camera.left = -20;
        key.shadow.camera.right = 20;
        key.shadow.camera.top = 20;
        key.shadow.camera.bottom = -20;
        this.scene.add(key);
        this.units = createUnitInstances(options.maxUnitInstances);
        this.root.add(this.units.mesh);
        this.horde = createHordeInstances(options.maxHordeInstances);
        this.root.add(this.horde.mesh);
        this.mecha = createMechaMesh();
        this.root.add(this.mecha.group);
        // Desire object: silver pedestal + holographic blue mecha (seen early)
        this.pedestalHolo = createPedestalHologram();
        this.root.add(this.pedestalHolo.group);
        this.combat = createCombatFx();
        this.root.add(this.combat.group);
        // Explosive canisters: red/purple barrels with lightning decals
        this.barrels = createBarrelSystem();
        this.root.add(this.barrels.group);
        // Pass-through gate shatter + floating count popup (gateBreak)
        this.gateBreak = createGateBreakFx();
        this.root.add(this.gateBreak.group);
        // Extra count popups for combat losses / absorb (beyond gate op text)
        this.countPopups = createCountPopupFx();
        this.root.add(this.countPopups.group);
        this.absorb = createAbsorbVfx();
        this.root.add(this.absorb.group);
        this.shake = createShakeState();
        this.setPath(40, this.pathHalfWidth);
    }
    /** Build / rebuild the greybox path-bridge mesh. */
    setPath(pathLength, halfWidth) {
        if (this.disposed)
            return;
        const length = Math.max(1, pathLength);
        const hw = halfWidth ?? this.pathHalfWidth;
        if (this.path && this.lastPathLength === length)
            return;
        this.path = rebuildPathBridge(this.root, this.path, {
            pathLength: length,
            halfWidth: hw,
        });
        this.lastPathLength = length;
    }
    /**
     * Place gate / wave / pedestal markers from level data.
     * Call once when a level loads (not every frame).
     * Spawns the silver pedestal + holographic mecha desire object at pedestal.z.
     */
    setLevelMarkers(input) {
        if (this.disposed)
            return;
        if (this.markers) {
            this.root.remove(this.markers.group);
            this.markers.dispose();
            this.markers = null;
        }
        this.markers = createLevelMarkers({
            ...input,
            pathHalfWidth: input.pathHalfWidth ?? this.pathHalfWidth,
        });
        this.root.add(this.markers.group);
        this.pedestalZ = input.pedestal?.z ?? null;
        this.pedestalX = 0;
        // Desire object always visible from far down the bridge (not cutscene-only)
        if (this.pedestalZ !== null) {
            this.pedestalHolo.setPose(this.pedestalX, this.pedestalZ);
            this.pedestalHolo.setVisible(true);
            this.pedestalHolo.setPedestalOnly(false);
            this.pedestalHolo.setSolidify(1);
            this.pedestalHolo.setAbsorbPulse(0);
            this.hologramSolidifyT = 1;
        }
        else {
            this.pedestalHolo.setVisible(false);
        }
        // Fresh level → reset gate break edge-detect + pools
        this.gateBreakPlayed.clear();
        this.gateBreak.reset();
        // Horde density from level wave HP (sim aggregate) — not gate decorations
        this.hordeWaves = (input.waves ?? []).map((w) => {
            const def = {
                id: w.id,
                z: w.z,
                maxHp: w.hp ?? w.maxHp ?? 40,
                ...(typeof w.contactHalfLength === "number"
                    ? { contactHalfLength: w.contactHalfLength }
                    : {}),
            };
            return def;
        });
        // Explosive canisters along the run (level-keyed from wave positions)
        const placements = defaultCanisterPlacementsForWaves(this.hordeWaves.map((w) => ({ id: w.id, z: w.z })), input.pathHalfWidth ?? this.pathHalfWidth);
        this.barrels.setPlacements(placements);
        this.barrels.reset();
        this.lastDetonations = [];
    }
    /**
     * Fire gateBreak shatter + count popup for a gate id (from markers).
     * Safe to call multiple times — plays once per gate id until markers reset.
     */
    notifyGateBreak(gateId) {
        if (this.disposed || !this.markers)
            return;
        if (this.gateBreakPlayed.has(gateId))
            return;
        const info = this.markers.getGateWorld(gateId);
        if (!info)
            return;
        this.gateBreakPlayed.add(gateId);
        this.gateBreak.triggerGateBreak({
            x: info.x,
            y: info.y,
            z: info.z,
            op: info.op,
            k: info.k,
            color: info.color,
        });
    }
    /**
     * Fire transform juice (absorb + FOV punch). Call from shell when sim
     * emits a `transform` event — safe to call once per transform.
     */
    notifyTransform(centroidX, pedestalZ) {
        if (this.disposed)
            return;
        const z = pedestalZ ?? this.pedestalZ ?? 0;
        triggerTransformJuice(this.juice, centroidX, z);
        // Screen shake on transform chorus
        triggerShake(this.shake, 0.72);
        // Hologram densifies then yields to the live sim mecha
        this.hologramSolidifyT = 1;
        this.pedestalHolo.setAbsorbPulse(1);
        this.pedestalHolo.setSolidify(1);
        this.pedestalHolo.setPedestalOnly(false);
    }
    /**
     * Trigger screen shake (identifier: shake). Strength ~0.15–0.8.
     * Used for explosions, horde contact, and transform.
     */
    triggerShake(amount) {
        if (this.disposed)
            return;
        triggerShake(this.shake, amount);
    }
    /**
     * Brief squad flash/tint when the horde connects (or combat drains N).
     * @param amount peak flash 0–1 (default 1)
     */
    pulseHitFlash(amount = 1) {
        if (this.disposed)
            return;
        this.hitFlashT = Math.max(this.hitFlashT, Math.min(1, amount));
        this.units.setHitFlash(this.hitFlashT);
    }
    /**
     * Floating count popup for a count delta (combat loss, absorb, extra gain).
     * Gates already pop via gateBreak; use this for every other N change.
     */
    notifyCountDelta(opts) {
        if (this.disposed)
            return;
        const snap = this.lastSnapshot;
        const cx = opts.x ?? snap?.lateralX ?? this.follow.x;
        const cz = opts.z ?? snap?.pathZ ?? this.follow.z;
        this.countPopups.spawn({
            x: cx,
            y: opts.y ?? 1.6,
            z: cz,
            delta: opts.delta,
            ...(opts.kind !== undefined ? { kind: opts.kind } : {}),
            ...(opts.text !== undefined ? { text: opts.text } : {}),
        });
    }
    /** Read-only juice state (hitstop remaining for RunLoop pause). */
    getTransformJuice() {
        return this.juice;
    }
    /** Read-only shake trauma (for tests / debug). */
    getShakeTrauma() {
        return this.shake.trauma;
    }
    /**
     * Apply a sim snapshot. Pure presentation mapping:
     * centroid ← (lateralX, pathZ), units ← formation slots, mecha visibility,
     * pedestal hologram spin/solidify, auto-fire, absorb beams, climax stomp.
     */
    applySnapshot(snap, dtSeconds = 1 / 60) {
        if (this.disposed)
            return;
        this.lastSnapshot = snap;
        const dt = Math.max(0, dtSeconds);
        this.displayTime += dt;
        // Edge-detect mecha spawn if shell forgot to call notifyTransform
        if (snap.inMecha && !this.wasInMecha) {
            if (!this.juice.mechaSpawned || this.juice.absorbAge === null) {
                triggerTransformJuice(this.juice, snap.lateralX, this.pedestalZ ?? snap.pathZ);
            }
            // Begin hologram → solid mecha solidify
            this.hologramSolidifyT = 1;
        }
        if (!snap.inMecha) {
            this.wasInMecha = false;
            this.juice.mechaSpawned = false;
            // Reset hologram for next run / continue
            if (this.hologramSolidifyT < 1 && this.pedestalZ !== null) {
                this.hologramSolidifyT = 1;
                this.pedestalHolo.setPedestalOnly(false);
                this.pedestalHolo.setSolidify(1);
            }
        }
        else {
            this.wasInMecha = true;
        }
        // Advance FOV / absorb (hitstop is owned by shell; still tick visuals)
        if (this.juice.fovPunchT > 0) {
            this.juice.fovPunchT = Math.max(0, this.juice.fovPunchT - dt / 0.42);
        }
        if (this.juice.absorbAge !== null) {
            this.juice.absorbAge += dt;
            if (this.juice.absorbAge >= ABSORB_DURATION_SECONDS) {
                this.juice.absorbAge = null;
            }
        }
        if (this.juice.hitstopRemaining > 0) {
            this.juice.hitstopRemaining = Math.max(0, this.juice.hitstopRemaining - dt);
        }
        // Hologram solidifies into real mecha during transform juice window
        if (snap.inMecha && this.hologramSolidifyT > 0) {
            this.hologramSolidifyT = Math.max(0, this.hologramSolidifyT - dt / HOLOGRAM_SOLIDIFY_SECONDS);
        }
        applyCameraFov(this.camera, currentFov(this.juice));
        // Screen shake trauma decay (applied to camera after follow)
        advanceShake(this.shake, dt);
        // Squad hit flash decay
        if (this.hitFlashT > 0) {
            this.hitFlashT = Math.max(0, this.hitFlashT - dt / 0.18);
            this.units.setHitFlash(this.hitFlashT);
        }
        if (snap.pathLength > 0 && snap.pathLength !== this.lastPathLength) {
            this.setPath(snap.pathLength, this.pathHalfWidth);
        }
        const centroid = centroidFromSnapshot(snap);
        updateCentroidFollow(this.camera, this.follow, centroid, dt, this.cameraOpts);
        // Apply damped shake offset on top of follow (identifier: shake)
        const shakeOff = sampleShakeOffset(this.shake, this.displayTime);
        this.camera.position.x += shakeOff.x;
        this.camera.position.y += shakeOff.y;
        this.camera.position.z += shakeOff.z;
        // Shared clock for combat FX + soldier run cycle + hologram spin
        const time = typeof snap.time === "number" ? snap.time : this.displayTime;
        // Pedestal + hologram: always on when level has a pedestal (desire object)
        if (this.pedestalZ !== null) {
            this.pedestalHolo.setPose(this.pedestalX, this.pedestalZ);
            this.pedestalHolo.setVisible(true);
            if (snap.inMecha) {
                this.pedestalHolo.setSolidify(this.hologramSolidifyT);
                // After solidify completes, keep silver platform only
                if (this.hologramSolidifyT <= 0.02) {
                    this.pedestalHolo.setPedestalOnly(true);
                }
            }
            else {
                this.pedestalHolo.setPedestalOnly(false);
                this.pedestalHolo.setSolidify(1);
            }
            // Absorb pulse as squad nears pedestal or during transform
            const distToPed = this.pedestalZ !== null ? Math.abs(centroid.z - this.pedestalZ) : 99;
            const nearPulse = !snap.inMecha && distToPed < 8
                ? Math.max(0, 1 - distToPed / 8) * 0.55
                : 0;
            const absorbPulse = this.juice.absorbAge !== null
                ? 1 - Math.min(1, this.juice.absorbAge / ABSORB_DURATION_SECONDS)
                : nearPulse;
            this.pedestalHolo.setAbsorbPulse(absorbPulse);
            this.pedestalHolo.update(dt, time);
        }
        if (snap.inMecha) {
            this.units.setVisible(false);
            this.units.apply([], 0, centroid.x, centroid.z, time);
            this.lastDrawn = 0;
            this.mecha.setVisible(true);
            this.mecha.setPose(centroid.x, centroid.z);
            // Spawn bloom from remaining FOV punch envelope
            this.mecha.setSpawnBloom(this.juice.fovPunchT);
        }
        else {
            this.mecha.setVisible(false);
            this.mecha.setSpawnBloom(0);
            this.mecha.setClimaxMotion(time, 0);
            this.units.setVisible(true);
            this.lastDrawn = this.units.apply(snap.formation, snap.n, centroid.x, centroid.z, time);
        }
        // Visible zombie horde: stream down the bridge toward the squad.
        // Density scales with sim wave HP pressure; contact kills thin the wall.
        // In mecha climax, HP drain → mass despawn bursts (scale inversion).
        this.lastHordeDrawn = this.horde.apply({
            waves: this.hordeWaves,
            activeWaveId: snap.activeWaveId ?? null,
            activeWaveHp: snap.activeWaveHp ?? 0,
            activeWaveMaxHp: snap.activeWaveMaxHp ?? 0,
            centroidX: centroid.x,
            centroidZ: centroid.z,
            timeSeconds: time,
            dtSeconds: dt,
            pathHalfWidth: this.pathHalfWidth,
            squadDrawn: this.lastDrawn,
            inMecha: snap.inMecha,
        });
        // Absorb VFX: soldiers stream/beam into pedestal (consume-N)
        if (this.juice.absorbAge !== null) {
            const pedX = this.juice.absorbX;
            const pedZ = this.juice.absorbZ;
            // Formation slots → world beam origins (scratch buffer; no per-frame alloc)
            const form = snap.formation ?? [];
            const nBeams = Math.min(16, form.length);
            for (let bi = 0; bi < nBeams; bi++) {
                const s = form[bi];
                const o = this.beamOriginScratch[bi];
                o.x = centroid.x + s.x;
                o.y = 0.85;
                o.z = centroid.z + s.z;
            }
            // If formation empty (already in mecha), procedural beams fill in
            this.absorb.update({
                active: true,
                ageSeconds: this.juice.absorbAge,
                x: pedX,
                z: pedZ,
                drainN: snap.n ?? 0,
                ...(nBeams > 0
                    ? {
                        beamOrigins: this.beamOriginScratch,
                        beamOriginCount: nBeams,
                    }
                    : {}),
            });
        }
        else {
            this.absorb.update(false, 0, 0, 0);
        }
        // Gate hide + gateBreak shatter / count popup on first contact
        if (this.markers && snap.triggeredGateIds) {
            for (const id of snap.triggeredGateIds) {
                if (!this.gateBreakPlayed.has(id)) {
                    this.notifyGateBreak(id);
                }
                this.markers.setTriggeredGate(id, true);
            }
        }
        this.gateBreak.update(dt);
        this.countPopups.update(dt);
        // Edge-detect combat N drain for hit flash + shake (popups come from shell
        // on n_changed combat, and from gateBreak on gates — avoid double text).
        if (!snap.inMecha && typeof snap.n === "number") {
            if (this.lastCountN !== null &&
                snap.n < this.lastCountN &&
                snap.activeWaveId) {
                const delta = snap.n - this.lastCountN;
                this.pulseHitFlash(Math.min(1, 0.55 + Math.min(0.45, -delta / 12)));
                triggerShake(this.shake, Math.min(0.45, 0.15 + -delta * 0.04));
            }
            this.lastCountN = snap.n;
        }
        else if (snap.inMecha) {
            this.lastCountN = null;
        }
        // Auto-fire combat visuals: per-soldier muzzle + pooled tracers + HP-delta hits
        // (presentation of sim aggregate DPS only — no damage logic here)
        const waveId = snap.activeWaveId ?? null;
        const dps = snap.friendlyDps ?? 0;
        const firing = waveId !== null &&
            dps > 0 &&
            (snap.activeWaveHp === undefined || snap.activeWaveHp > 0);
        let toZ = centroid.z + 6;
        if (firing && this.markers && waveId) {
            const wz = this.markers.getWaveZ(waveId);
            if (wz !== null)
                toZ = wz;
        }
        else if (firing && snap.pathZ !== undefined) {
            toZ = snap.pathZ + 5;
        }
        const hpFrac = snap.activeWaveMaxHp && snap.activeWaveMaxHp > 0
            ? 1 - (snap.activeWaveHp ?? 0) / snap.activeWaveMaxHp
            : 0.5;
        const mechaBoost = snap.inMecha ? 1.35 : 1;
        const intensity = firing
            ? (0.4 + 0.6 * Math.min(1, dps / (snap.inMecha ? 200 : 40))) * mechaBoost
            : 0;
        const fireIntensity = intensity * (0.5 + 0.5 * hpFrac);
        this.combat.update({
            firing,
            centroidX: centroid.x,
            centroidZ: centroid.z,
            toZ,
            timeSeconds: time,
            intensity: fireIntensity,
            formation: snap.formation,
            n: snap.n,
            ...(typeof snap.activeWaveHp === "number"
                ? { activeWaveHp: snap.activeWaveHp }
                : {}),
            inMecha: snap.inMecha,
        });
        // Explosive canisters: heat under squad fire → fireball/spark/scorch + chain.
        // Heat whenever the squad has DPS (not only contact) so roadside canisters
        // ahead of a wave can pop as the fire cone reaches them.
        const canisterFire = dps > 0 && (snap.n > 0 || snap.inMecha) && snap.phase !== "results_lose";
        this.lastDetonations = this.barrels.update({
            firing: canisterFire,
            intensity: Math.max(fireIntensity, canisterFire ? (snap.inMecha ? 1.15 : 0.75) : 0),
            centroidX: centroid.x,
            centroidZ: centroid.z,
            dtSeconds: dt,
            timeSeconds: time,
            activeWaveId: waveId,
        });
        // Explosion shake (canister fireball)
        if (this.lastDetonations.length > 0) {
            const n = this.lastDetonations.length;
            triggerShake(this.shake, Math.min(0.85, 0.42 + n * 0.12));
        }
        // Mecha climax: rocket pulse + stomp while deleting the horde
        if (snap.inMecha && firing) {
            this.rocketAcc += dt;
            if (this.rocketAcc >= 0.22) {
                this.rocketAcc = 0;
                this.mecha.pulseRocket(0.7 + 0.3 * intensity);
            }
            this.mecha.setClimaxMotion(time, Math.min(1, 0.55 + intensity * 0.45));
        }
        else if (snap.inMecha) {
            this.mecha.setClimaxMotion(time, 0.15);
        }
    }
    /**
     * Detonations from the last applySnapshot frame.
     * Shell applies each event's damage via SIM Model B wave HP
     * (same aggregate pool resolveCombatTick writes).
     */
    drainCanisterDetonations() {
        const events = this.lastDetonations;
        this.lastDetonations = [];
        return events;
    }
    /** Instantly snap camera/follow to snapshot centroid (run start / seek). */
    snapToSnapshot(snap) {
        if (this.disposed)
            return;
        this.lastSnapshot = snap;
        this.wasInMecha = snap.inMecha;
        this.juice.hitstopRemaining = 0;
        this.juice.fovPunchT = 0;
        this.juice.absorbAge = null;
        this.juice.mechaSpawned = snap.inMecha;
        // Reset hologram solidify for a fresh run (or hide if already in mecha)
        this.hologramSolidifyT = snap.inMecha ? 0 : 1;
        if (this.pedestalZ !== null) {
            this.pedestalHolo.setPose(this.pedestalX, this.pedestalZ);
            this.pedestalHolo.setVisible(true);
            this.pedestalHolo.setSolidify(this.hologramSolidifyT);
            this.pedestalHolo.setPedestalOnly(snap.inMecha);
            this.pedestalHolo.setAbsorbPulse(0);
        }
        // Run start / seek: clear gateBreak / count popup / shake juice
        this.gateBreakPlayed.clear();
        this.gateBreak.reset();
        this.countPopups.reset();
        resetShake(this.shake);
        this.hitFlashT = 0;
        this.units.setHitFlash(0);
        this.lastCountN = typeof snap.n === "number" ? snap.n : null;
        this.barrels.reset();
        this.lastDetonations = [];
        applyCameraFov(this.camera, this.baseFov);
        if (snap.pathLength > 0) {
            this.setPath(snap.pathLength, this.pathHalfWidth);
        }
        const centroid = centroidFromSnapshot(snap);
        snapCentroidFollow(this.camera, this.follow, centroid, this.cameraOpts);
        this.applySnapshot(snap, 0);
    }
    /** Resize renderer + portrait camera aspect (landscape FOV adapts; no letterbox). */
    setSize(width, height) {
        if (this.disposed)
            return;
        const w = Math.max(1, Math.floor(width));
        const h = Math.max(1, Math.floor(height));
        this.renderer.setSize(w, h, false);
        setPortraitAspect(this.camera, w, h, this.cameraOpts);
        // Keep juice base FOV in sync with aspect so punches settle correctly
        this.baseFov = portraitFovForAspect(w / h, this.cameraOpts);
        this.juice.baseFov = this.baseFov;
    }
    /** Draw one frame. */
    render() {
        if (this.disposed)
            return;
        this.renderer.info.reset();
        this.renderer.render(this.scene, this.camera);
        this.lastDrawCalls = this.renderer.info.render.calls;
        this.lastTriangles = this.renderer.info.render.triangles;
    }
    getCentroid() {
        return { x: this.follow.x, y: this.follow.y, z: this.follow.z };
    }
    getLastDrawnInstanceCount() {
        return this.lastDrawn;
    }
    /** Living zombie instances drawn last frame (LOD-clamped horde pool). */
    getLastHordeDrawnCount() {
        return this.lastHordeDrawn;
    }
    /** WebGL draw calls from the last render() (after info.reset). */
    getLastDrawCallCount() {
        return this.lastDrawCalls;
    }
    /** Triangle count submitted in the last render(). */
    getLastTriangleCount() {
        return this.lastTriangles;
    }
    getLastSnapshot() {
        return this.lastSnapshot;
    }
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        if (this.path) {
            this.root.remove(this.path.group);
            this.path.dispose();
            this.path = null;
        }
        if (this.markers) {
            this.root.remove(this.markers.group);
            this.markers.dispose();
            this.markers = null;
        }
        this.combat.dispose();
        this.barrels.dispose();
        this.gateBreak.dispose();
        this.countPopups.dispose();
        this.absorb.dispose();
        this.pedestalHolo.dispose();
        this.horde.dispose();
        this.units.dispose();
        this.mecha.dispose();
        this.renderer.dispose();
        this.scene.clear();
    }
}
//# sourceMappingURL=GreyboxRenderer.js.map